import { type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { Button } from '../primitives/Button';
import { Modal } from '../primitives/Modal';
import { Switch } from '../primitives/Switch';
import { ResourceIcon } from './ResourceIcon';
import { formatExact } from '../../lib/format';

export interface PremiumConfirmProps {
  open: boolean;
  /** What is being bought, e.g. "Titan-Schmelze Stufe 7 sofort fertigstellen". */
  subject: ReactNode;
  /** Full price in Chronos crystals. Never a partial price. */
  cost: number;
  /** Crystals the player holds right now. */
  balance: number;
  /** What the player gets — one sentence, concrete. */
  effect?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
  className?: string;
  /**
   * Optional "skip this dialog next time" toggle — the caller owns the
   * persisted preference (Settings), this component only renders the row and
   * reports the checked state back. Omit entirely for confirmations that
   * should never be skippable (e.g. the Chronos-Shop checkout).
   */
  skipNextTime?: {
    checked: boolean;
    onChange: (checked: boolean) => void;
  };
}

/**
 * The gate in front of every Chronos-crystal purchase.
 *
 * Hard currency never leaves an account on a single click — the owner's rule,
 * and the right one. Three things are always on screen before the player can
 * confirm: the full price, what it buys, and what the balance will be
 * afterwards. Cancel is the wider, calmer button; confirm is the deliberate one.
 */
export function PremiumConfirm({
  open,
  subject,
  cost,
  balance,
  effect,
  onConfirm,
  onCancel,
  className,
  skipNextTime,
}: PremiumConfirmProps) {
  const affordable = balance >= cost;
  const after = balance - cost;

  return (
    <Modal
      open={open}
      onClose={onCancel}
      size="sm"
      eyebrow="Premium-Währung"
      title="Chronos-Kristalle ausgeben?"
      className={className}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            Abbrechen
          </Button>
          <Button variant="premium" onClick={onConfirm} disabled={!affordable}>
            {affordable ? `${cost} Kristalle ausgeben` : 'Nicht genug Kristalle'}
          </Button>
        </>
      }
    >
      <p className="text-xs leading-relaxed text-content-secondary">{subject}</p>

      {effect ? (
        <div className="mt-3 rounded-control border border-chronos/30 bg-chronos/[0.06] px-2.5 py-2">
          <span className="font-display text-label uppercase text-chronos-bright">Wirkung</span>
          <p className="mt-0.5 text-xs text-content">{effect}</p>
        </div>
      ) : null}

      <dl className="mt-3 space-y-1.5 rounded-control border border-edge-subtle bg-surface-2 px-3 py-2.5">
        <Row label="Kosten">
          <span className="inline-flex items-center gap-1 text-chronos-bright">
            <ResourceIcon resource="chronos" size={16} />
            {formatExact(cost)}
          </span>
        </Row>
        <Row label="Aktuell">{formatExact(balance)}</Row>
        <Row label="Danach">
          <span className={cn(affordable ? 'text-content' : 'text-danger-bright')}>
            {formatExact(Math.max(0, after))}
          </span>
        </Row>
      </dl>

      {!affordable ? (
        <p className="mt-2 text-xs leading-relaxed text-danger-bright">
          Dir fehlen {formatExact(cost - balance)} Chronos-Kristalle.
        </p>
      ) : null}

      {skipNextTime ? (
        <div className="mt-3 border-t border-edge-subtle pt-3">
          <Switch
            id="premium-confirm-skip"
            checked={skipNextTime.checked}
            onChange={skipNextTime.onChange}
            label="Nicht mehr vor jedem Zeitsprung fragen"
            description={
              skipNextTime.checked
                ? 'Du kannst diese Bestätigung jederzeit in den Einstellungen wieder aktivieren.'
                : undefined
            }
          />
        </div>
      ) : null}
    </Modal>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="font-display text-label-sm uppercase text-content-muted">{label}</dt>
      <dd data-sg-numeric className="text-xs font-semibold text-content">
        {children}
      </dd>
    </div>
  );
}
